// Chat-history persistence client — talks to backend/routers/chat_history.py.
// Authenticated with the dashboard session token (not the X-API-Key).

import { config } from "../config";
import type { ChatData, ChatSession } from "../types";
import { getStoredToken } from "./auth";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getStoredToken();

  const res = await fetch(`${config.apiBase}/chat-history${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!res.ok) throw new Error(`Chat history request failed (${res.status})`);

  return res.json() as Promise<T>;
}

export async function fetchSessions(): Promise<ChatSession[]> {
  const body = await request<{ sessions: ChatSession[] }>("/sessions");
  return body.sessions;
}

export async function createRemoteSession(title: string): Promise<string> {
  const body = await request<{ id: string }>("/sessions", {
    method: "POST",
    body: JSON.stringify({ title }),
  });
  return body.id;
}

export async function deleteRemoteSession(sessionId: string): Promise<void> {
  await request(`/sessions/${sessionId}`, { method: "DELETE" });
}

export async function saveQuery(sessionId: string, content: string): Promise<string> {
  const body = await request<{ id: string }>(`/sessions/${sessionId}/queries`, {
    method: "POST",
    body: JSON.stringify({ content }),
  });
  return body.id;
}

export async function saveReply(sessionId: string, queryId: string, content: string, data: ChatData): Promise<void> {
  await request(`/sessions/${sessionId}/queries/${queryId}/message`, {
    method: "POST",
    body: JSON.stringify({ content, data }),
  });
}
